// src/features/dashboard/components/StatCard.tsx

import React from 'react';
import { motion } from 'framer-motion';
import Skeleton from '../../../components/Skeleton';

interface StatCardProps {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  bgColor: string;
  textColor: string;
  onClick: () => void;
  index: number;
  loading?: boolean;
}

// Single stat card component with animation
export const StatCard = React.memo<StatCardProps>(({
  title,
  value,
  subtitle,
  icon: Icon,
  bgColor,
  textColor,
  onClick,
  index,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="theme-card rounded-lg p-5 border theme-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" width="60%" height={16} />
            <Skeleton variant="text" width="40%" height={32} />
          </div>
          <Skeleton variant="rounded" width={48} height={48} />
        </div>
        <Skeleton variant="text" width="80%" height={12} />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      onClick={onClick}
      className="theme-card rounded-lg p-5 cursor-pointer hover:shadow-md transition-all duration-200 border theme-border"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="theme-card-subtitle text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold theme-card-title">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${bgColor}`}>
          <Icon className={`w-6 h-6 ${textColor}`} />
        </div>
      </div>
      <p className="text-xs theme-card-subtitle">{subtitle}</p>
    </motion.div>
  );
});

StatCard.displayName = 'StatCard';
